import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { queryClient } from "./lib/react-query";
import { CookieConsent } from "./components/CookieConsent";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { CourseErrorBoundary } from "./components/CourseErrorBoundary";
import { ChatErrorBoundary } from "./components/ChatErrorBoundary";
import { FullPageSkeleton } from "./components/LoadingSkeleton";
import { RouteTransition } from "./components/RouteTransition";

// Lazy load pages for code splitting
const WelcomePage = lazy(() => import("./pages/WelcomePage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AppLayout = lazy(() => import("./pages/AppLayout"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const NotesPage = lazy(() => import("./pages/NotesPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const GroupDetailPage = lazy(() => import("./pages/GroupDetailPage"));
const LearningPathPage = lazy(() => import("./pages/LearningPathPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

// Loading component for lazy routes
const PageLoader = () => <RouteTransition />;

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <FullPageSkeleton />;
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <WelcomePage />} />
        <Route path="/auth" element={user ? <Navigate to="/dashboard" replace /> : <AuthPage />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        
        {/* Protected app routes with error boundaries */}
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route 
            path="/courses" 
            element={
              <CourseErrorBoundary>
                <CoursesPage />
              </CourseErrorBoundary>
            } 
          />
          <Route path="/notes" element={<NotesPage />} />
          <Route 
            path="/chat" 
            element={
              <ChatErrorBoundary>
                <ChatPage />
              </ChatErrorBoundary>
            } 
          />
          <Route path="/groups/:groupId" element={<GroupDetailPage />} />
          <Route path="/learning-path" element={<LearningPathPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>
        
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <AppRoutes />
                <CookieConsent />
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
          {/* React Query DevTools - only shown in development */}
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
